import React, { createContext, useContext, useState, useEffect } from 'react';

    const AppContext = createContext();

    export const useApp = () => {
      const context = useContext(AppContext);
      if (!context) {
        throw new Error('useApp must be used within an AppProvider');
      }
      return context;
    };

    export const AppProvider = ({ children }) => {
      const [notifications, setNotifications] = useState([]);
      const [activeChat, setActiveChat] = useState(null);
      const [studyProgress, setStudyProgress] = useState({});
      const [showNotificationCenter, setShowNotificationCenter] = useState(false);

      const [theme, setTheme] = useState(() => {
        try {
          const savedTheme = localStorage.getItem('ilm_global_theme');
          return savedTheme || 'light';
        } catch (e) {
          console.error('Error accessing localStorage:', e);
          return 'light';
        }
      });

      const [fontSize, setFontSize] = useState(() => {
        try {
          return localStorage.getItem('ilm_global_fontSize') || 'medium';
        } catch (e) {
          return 'medium';
        }
      });

      const [language, setLanguage] = useState(() => {
        try {
          return localStorage.getItem('ilm_global_language') || 'en';
        } catch (e) {
          return 'en';
        }
      });

      useEffect(() => {
        try {
          const root = document.documentElement;
          if (theme === 'dark') {
            root.classList.add('dark');
          } else {
            root.classList.remove('dark');
          }
          localStorage.setItem('ilm_global_theme', theme);
          console.log('Theme applied:', theme);
        } catch (e) {
          console.error('Error applying theme:', e);
        }
      }, [theme]);

      useEffect(() => {
        try {
          const root = document.documentElement;
          switch (fontSize) {
            case 'small':
              root.style.fontSize = '0.9rem';
              break;
            case 'medium':
              root.style.fontSize = '1rem';
              break;
            case 'large':
              root.style.fontSize = '1.1rem';
              break;
            default:
              root.style.fontSize = '1rem';
          }
          localStorage.setItem('ilm_global_fontSize', fontSize);
        } catch (e) {
          console.error('Error applying font size:', e);
        }
      }, [fontSize]);

      useEffect(() => {
        try {
          localStorage.setItem('ilm_global_language', language);
        } catch (e) {
          console.error('Error saving language preference:', e);
        }
      }, [language]);

      const addNotification = (notification) => {
        const id = Date.now().toString();
        const newNotification = { ...notification, id, timestamp: new Date() };
        setNotifications(prev => [newNotification, ...prev]);
        setTimeout(() => {
          removeNotification(id);
        }, 5000);
      };

      const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
      };

      const clearAllNotifications = () => {
        setNotifications([]);
        setShowNotificationCenter(false);
      };

      const updateStudyProgress = (subject, progress) => {
        setStudyProgress(prev => ({ ...prev, [subject]: progress }));
      };

      const updateAppearance = (settings) => {
        if (settings.theme) {
          setTheme(settings.theme);
        }
        if (settings.fontSize) {
          setFontSize(settings.fontSize);
        }
        if (settings.language) {
          setLanguage(settings.language);
        }
      };

      const toggleNotificationCenter = () => {
        setShowNotificationCenter(prev => !prev);
      };

      useEffect(() => {
        const sampleNotifications = [
          { type: 'success', title: 'Welcome to ILM Global!', message: 'Complete your profile to get started with your learning journey', action: '/profile' },
          { type: 'info', title: 'New Quiz Available', message: 'A new Mathematics quiz has been added to the study room', action: '/study-room' },
          { type: 'warning', title: 'Study Reminder', message: 'You haven\'t completed any quizzes today. Keep up your learning streak!', action: '/study-room' }
        ];
        setTimeout(() => {
          sampleNotifications.forEach((notif) => {
            const id = Date.now().toString() + Math.random().toString(36).substring(2, 9);
            const newNotification = { ...notif, id, timestamp: new Date() };
            setNotifications(prev => [newNotification, ...prev]);
            setTimeout(() => {
              removeNotification(id);
            }, 30000);
          });
        }, 3000);
      }, []);

      const value = {
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        activeChat,
        setActiveChat,
        studyProgress,
        updateStudyProgress,
        theme,
        fontSize,
        language,
        updateAppearance,
        showNotificationCenter,
        setShowNotificationCenter,
        toggleNotificationCenter
      };

      return (
        <AppContext.Provider value={value}>
          {children}
        </AppContext.Provider>
      );
    };